import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { emit as emitEvent } from "@/lib/events";
import { X, Save, Loader2, Wand2, ChevronDown, ChevronUp } from "lucide-react";

const EMPTY = {
  project_title: "",
  customer: "",
  contract_number: "",
  contract_value: "",
  period_start: "",
  period_end: "",
  scope: "",
  naics_codes: "",
  keywords: "",
  poc_name: "",
  poc_email: "",
  poc_phone: "",
  outcome: "",
  cpars_rating: "",
};

const csv = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr || "");
const split = (s) =>
  (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Right-side slide-over that lets the operator quick-add a past performance
 * record without leaving the bid/no-bid screen. Prefills NAICS from the
 * opportunity so the AI's match score gets a boost immediately on save.
 *
 * Props:
 *   open          — bool
 *   onClose       — () => void
 *   noticeId      — string (used to prefill NAICS + keyword from the opp)
 */
export default function PastPerformanceSlideOver({ open, onClose, noticeId }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);

  // Fetch the opportunity's NAICS + title on open so we can prefill.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setForm(EMPTY);
    setShowMore(false);
    (async () => {
      try {
        const r = await api.get(`/opportunities/${noticeId}`);
        if (!alive) return;
        const opp = r.data || {};
        setForm((f) => ({
          ...f,
          naics_codes: opp.naics ? String(opp.naics) : "",
        }));
      } catch {
        /* silent — form still usable */
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, noticeId]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  const save = async () => {
    if (!form.project_title.trim()) {
      toast.error("Project title is required");
      return;
    }
    setSaving(true);
    const payload = {
      project_title: form.project_title.trim(),
      customer: form.customer || null,
      contract_number: form.contract_number || null,
      contract_value:
        form.contract_value === "" || form.contract_value == null ? null : Number(form.contract_value),
      period_start: form.period_start || null,
      period_end: form.period_end || null,
      scope: form.scope || null,
      naics_codes: split(form.naics_codes),
      keywords: split(form.keywords),
      poc_name: form.poc_name || null,
      poc_email: form.poc_email || null,
      poc_phone: form.poc_phone || null,
      outcome: form.outcome || null,
      cpars_rating: form.cpars_rating || null,
    };
    try {
      await api.post("/past-performance", payload);
      toast.success("Past performance added — rescoring…");
      emitEvent("past_performance:changed", { reason: "quick_add" });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="pp-quick-slideover">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={() => !saving && onClose?.()}
      />

      {/* Slide-over panel */}
      <div className="ml-auto relative h-full w-full max-w-xl bg-[#0D1424] border-l border-white/10 shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-400/15 text-amber-300 flex items-center justify-center">
              <Wand2 className="w-4 h-4" />
            </div>
            <div>
              <div className="mono-label text-amber-300">// QUICK ADD</div>
              <div className="font-display font-bold text-lg text-white">Past performance</div>
            </div>
          </div>
          <button
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            className="text-slate-400 hover:text-white disabled:opacity-40"
            data-testid="pp-quick-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <Field label="Project title *">
            <input
              value={form.project_title}
              onChange={(e) => setForm({ ...form, project_title: e.target.value })}
              className={inputCls}
              placeholder="Middle Residence Reroof — Camp Pendleton"
              data-testid="pp-quick-title"
              autoFocus
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer / agency">
              <input
                value={form.customer}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}
                className={inputCls}
                placeholder="Dept of the Navy"
                data-testid="pp-quick-customer"
              />
            </Field>
            <Field label="Contract value (USD)">
              <input
                type="number"
                value={form.contract_value}
                onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
                className={inputCls}
                placeholder="425000"
                data-testid="pp-quick-value"
              />
            </Field>
            <Field label="Period start">
              <input
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className={inputCls}
                placeholder="2023-08"
              />
            </Field>
            <Field label="Period end">
              <input
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className={inputCls}
                placeholder="2024-02"
              />
            </Field>
          </div>

          <Field label="Scope of work">
            <textarea
              value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value })}
              rows={4}
              className={inputCls}
              placeholder="Removed and replaced 42,000 sf of standing seam metal roofing across two barracks including new underlayment, flashing, and gutters."
              data-testid="pp-quick-scope"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="NAICS (comma-separated)">
              <input
                value={form.naics_codes}
                onChange={(e) => setForm({ ...form, naics_codes: e.target.value })}
                className={inputCls}
                placeholder="238160, 236220"
                data-testid="pp-quick-naics"
              />
            </Field>
            <Field label="Keywords / tags">
              <input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                className={inputCls}
                placeholder="metal roofing, standing seam"
                data-testid="pp-quick-keywords"
              />
            </Field>
          </div>

          <Field label="CPARS rating">
            <input
              value={form.cpars_rating}
              onChange={(e) => setForm({ ...form, cpars_rating: e.target.value })}
              className={inputCls}
              placeholder="Exceptional"
            />
          </Field>

          {/* Optional fields collapsed by default */}
          <button
            type="button"
            onClick={() => setShowMore((s) => !s)}
            className="text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-cyan-300 flex items-center gap-1"
            data-testid="pp-quick-toggle-more"
          >
            {showMore ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showMore ? "HIDE OPTIONAL FIELDS" : "SHOW MORE (POC / outcome / contract #)"}
          </button>

          {showMore && (
            <div className="space-y-4 pt-2 border-t border-white/5">
              <Field label="Contract number">
                <input
                  value={form.contract_number}
                  onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
                  className={inputCls}
                  placeholder="N62473-23-C-4501"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="POC name">
                  <input
                    value={form.poc_name}
                    onChange={(e) => setForm({ ...form, poc_name: e.target.value })}
                    className={inputCls}
                    placeholder="CDR John Smith"
                  />
                </Field>
                <Field label="POC email">
                  <input
                    value={form.poc_email}
                    onChange={(e) => setForm({ ...form, poc_email: e.target.value })}
                    className={inputCls}
                    placeholder="john.smith@navy.mil"
                  />
                </Field>
              </div>
              <Field label="POC phone">
                <input
                  value={form.poc_phone}
                  onChange={(e) => setForm({ ...form, poc_phone: e.target.value })}
                  className={inputCls}
                  placeholder="(760) 555-0101"
                />
              </Field>
              <Field label="Outcome / results">
                <textarea
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  rows={3}
                  className={inputCls}
                  placeholder="Delivered 12 days early with zero safety incidents. Received Exceptional CPARS."
                />
              </Field>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 px-6 py-4 flex items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-500">
            Saving auto-rescores the match preview on this screen.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => !saving && onClose?.()}
              disabled={saving}
              className="text-xs font-mono font-bold tracking-widest text-slate-400 hover:text-slate-200 px-3 py-2 disabled:opacity-40"
              data-testid="pp-quick-cancel"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              disabled={saving || !form.project_title.trim()}
              className="text-xs font-mono font-bold tracking-widest bg-amber-400 text-slate-900 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 px-5 py-2.5 rounded-full"
              data-testid="pp-quick-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "SAVING" : "SAVE & RESCORE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-amber-400 focus:bg-white/[0.05] outline-none";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
